/**
 * Cookie helpers for session storage.
 *
 * The session lives in cookies rather than localStorage so it is shared across
 * `www`, `api` and any future `app` subdomain — localStorage is origin-scoped
 * and would force a second login on each one.
 *
 * These use `document.cookie` rather than the newer CookieStore API on purpose:
 * CookieStore is async (so it could not back a synchronous first paint) and is
 * still unavailable in Safari. Biome's `noDocumentCookie` rule is disabled
 * project-wide for that reason.
 */

const APEX_DOMAIN = 'codeverse.com'

/**
 * Work out the Domain attribute for the current host.
 *
 * This must be derived, not hardcoded. Staging runs on `*.webflow.io` and dev
 * on `localhost`, both different registrable domains — a browser rejects a
 * `Domain=.codeverse.com` cookie outright from either, and the session would
 * silently fail to persist. Returning null means "host-only cookie", which is
 * the correct behaviour everywhere except production.
 */
export function cookieDomain(hostname: string): string | null {
  // Exact match or a true subdomain — never a suffix match, which would also
  // accept something like `evilcodeverse.com`.
  if (hostname === APEX_DOMAIN || hostname.endsWith(`.${APEX_DOMAIN}`)) {
    return `.${APEX_DOMAIN}`
  }
  return null
}

export interface CookieOptions {
  maxAgeSeconds?: number
  hostname?: string
  protocol?: string
}

/**
 * Read a cookie, treating an empty value as absent.
 *
 * Deleting a cookie by setting `Max-Age=0` removes it outright in a browser,
 * but some environments (happy-dom, and some server-side cookie jars) keep the
 * name with an empty value. Since none of these cookies are ever meaningfully
 * empty — an empty access token is not a session — collapsing both cases to
 * null means callers can't accidentally treat a cleared cookie as present.
 */
export function readCookie(name: string, cookieString: string = document.cookie): string | null {
  for (const part of cookieString.split(';')) {
    const separator = part.indexOf('=')
    if (separator === -1) continue
    if (part.slice(0, separator).trim() !== name) continue

    const raw = part.slice(separator + 1).trim()
    if (raw === '') return null

    try {
      return decodeURIComponent(raw)
    } catch {
      return raw
    }
  }
  return null
}

/** Build a Set-Cookie-style string. Exported separately so it can be tested. */
export function serialiseCookie(name: string, value: string, options: CookieOptions = {}): string {
  const { maxAgeSeconds, hostname = location.hostname, protocol = location.protocol } = options

  const parts = [`${name}=${encodeURIComponent(value)}`, 'Path=/', 'SameSite=Lax']

  const domain = cookieDomain(hostname)
  if (domain) parts.push(`Domain=${domain}`)

  // Safari refuses Secure cookies over plain http, which would break the local
  // dev harness. Production and the webflow.io staging domain are both https.
  if (protocol === 'https:') parts.push('Secure')

  if (maxAgeSeconds !== undefined) parts.push(`Max-Age=${Math.max(0, Math.floor(maxAgeSeconds))}`)

  return parts.join('; ')
}

export function writeCookie(name: string, value: string, options: CookieOptions = {}): void {
  document.cookie = serialiseCookie(name, value, options)
}

export function deleteCookie(name: string, options: CookieOptions = {}): void {
  document.cookie = serialiseCookie(name, '', { ...options, maxAgeSeconds: 0 })
}
